// src/pages/qc/RmAnalysisModal.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { todayDubai } from "../../utils/dateUtils";

const EMPTY_HEADER = {
  date:           todayDubai(),
  rmId:           "",
  invoiceNo:      "",
  batchNo:        "",
  productionDate: "",
  expiryDate:     "",
  remarks:        "",
};

function Row({ label, children }) {
  return (
    <div className="form-row">
      <span className="form-label">{label}</span>
      <div style={{ display:"flex", gap:6, alignItems:"center", flex:1 }}>{children}</div>
    </div>
  );
}

export function RmAnalysisModal({ rms, onSave, onCancel }) {
  const [header,      setHeader]      = useState(EMPTY_HEADER);
  const [specParams,  setSpecParams]  = useState([]);  // [{parameter, uom, minValue, maxValue, expectedValue}]
  const [measured,    setMeasured]    = useState({});   // {paramName: value}
  const [loadingSpec, setLoadingSpec] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);

  function setH(field, value) { setHeader(h => ({ ...h, [field]: value })); }

  useEffect(() => {
    if (!header.rmId) { setSpecParams([]); setMeasured({}); return; }
    setLoadingSpec(true);
    api.fetchRmSpec(header.rmId)
      .then(res => {
        setSpecParams(res.params ?? []);
        setMeasured({});
      })
      .catch(() => setSpecParams([]))
      .finally(() => setLoadingSpec(false));
  }, [header.rmId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!header.rmId)                 { setError("Product is required.");         return; }
    if (!header.invoiceNo.trim())     { setError("Invoice No is required.");       return; }
    if (!header.batchNo.trim())       { setError("Batch No is required.");         return; }
    if (!header.productionDate)       { setError("Production Date is required.");  return; }
    if (!header.expiryDate)           { setError("Expiry Date is required.");      return; }
    setSaving(true); setError(null);
    try {
      await onSave({
        date:           header.date,
        rmId:           header.rmId,
        invoiceNo:      header.invoiceNo.trim(),
        batchNo:        header.batchNo.trim(),
        productionDate: header.productionDate,
        expiryDate:     header.expiryDate,
        params:         measured,
        remarks:        header.remarks.trim() || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const sortedRms = [...rms].sort((a, b) => a.id.localeCompare(b.id));

  function specHint(p) {
    if (p.minValue != null && p.maxValue != null) return `${p.minValue} – ${p.maxValue}${p.uom ? " " + p.uom : ""}`;
    if (p.minValue != null) return `≥ ${p.minValue}${p.uom ? " " + p.uom : ""}`;
    if (p.maxValue != null) return `≤ ${p.maxValue}${p.uom ? " " + p.uom : ""}`;
    if (p.expectedValue)    return `Expected: ${p.expectedValue}`;
    return "";
  }

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">Add RM Analysis</h3>
        <button className="btn" onClick={onCancel}>✕</button>
      </div>

      <form onSubmit={handleSubmit}>
        <Row label="Analysis Date">
          <input type="date" className="form-input" value={header.date}
            onChange={e => setH("date", e.target.value)} required style={{ width:160 }} />
        </Row>

        <Row label="Product *">
          <select className="form-select" value={header.rmId}
            onChange={e => setH("rmId", e.target.value)} style={{ flex:1 }}>
            <option value="">— select raw material —</option>
            {sortedRms.map(r => (
              <option key={r.id} value={r.id}>{r.id} — {r.name}</option>
            ))}
          </select>
        </Row>

        <Row label="Invoice No *">
          <input className="form-input" placeholder="e.g. INV-2025-001" value={header.invoiceNo}
            onChange={e => setH("invoiceNo", e.target.value)} style={{ flex:1 }} />
        </Row>

        <Row label="Batch No *">
          <input className="form-input" placeholder="e.g. B-2501" value={header.batchNo}
            onChange={e => setH("batchNo", e.target.value)} style={{ flex:1 }} />
        </Row>

        <Row label="Production Date *">
          <input type="date" className="form-input" value={header.productionDate}
            onChange={e => setH("productionDate", e.target.value)} required style={{ width:160 }} />
        </Row>

        <Row label="Expiry Date *">
          <input type="date" className="form-input" value={header.expiryDate}
            onChange={e => setH("expiryDate", e.target.value)} required style={{ width:160 }} />
        </Row>

        {header.rmId && (
          <>
            <div className="form-sep" />
            <div style={{ fontSize:11, fontWeight:700, color:"#888", marginBottom:8,
              textTransform:"uppercase", letterSpacing:".05em" }}>
              Parameters
            </div>

            {loadingSpec && (
              <div style={{ color:"#aaa", fontSize:12, padding:"8px 0" }}>Loading spec…</div>
            )}

            {!loadingSpec && specParams.length === 0 && (
              <div className="alert-warn" style={{ margin:"0 0 8px" }}>
                No spec defined for this RM — you can still enter results below.
              </div>
            )}

            {!loadingSpec && specParams.map(p => (
              <Row key={p.parameter} label={p.parameter}>
                <input className="form-input" value={measured[p.parameter] ?? ""}
                  onChange={e => setMeasured(m => ({ ...m, [p.parameter]: e.target.value }))}
                  placeholder={specHint(p) || "measured value"}
                  style={{ width:160 }} />
                {specHint(p) && (
                  <span style={{ fontSize:11, color:"#aaa" }}>spec: {specHint(p)}</span>
                )}
              </Row>
            ))}

            {!loadingSpec && specParams.length === 0 && (
              <Row label="Result">
                <input className="form-input" placeholder="Enter result…"
                  value={measured["Result"] ?? ""}
                  onChange={e => setMeasured(m => ({ ...m, Result: e.target.value }))}
                  style={{ flex:1 }} />
              </Row>
            )}
          </>
        )}

        <div className="form-sep" />

        <Row label="Remarks">
          <textarea className="form-input" rows={2} placeholder="Optional notes…"
            value={header.remarks} onChange={e => setH("remarks", e.target.value)}
            style={{ flex:1, resize:"vertical" }} />
        </Row>

        {error && <div className="alert-warn" style={{ marginTop:8 }}>{error}</div>}

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
